import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";

import { searchFullTextHeadlines } from "./api";
import headlinesSSEClient from "./headlinesSSEClient";
import useAuth from "@/hooks/useAuth";
import type { Headline } from "@/widget-components/newsquawk-headlines/models/Headline";
import type {
  SearchResponse,
  SearchConfig,
} from "@/widget-components/newsquawk-headlines/api/types";
import type { ConnectionStatus } from "@/shared/services/configurable-sse-client";
import { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import {
  headlineFilterMatcher,
  headlinePortfolioMatcher,
} from "@/widget-components/newsquawk-headlines/utils";

/** Maximum headlines to keep in the first page of search results */
const MAX_FIRST_PAGE = 200;

interface UseSearchHeadlinesOptions {
  subscribe?: boolean;
}

/**
 * Create a stable string key from a SearchConfig for use as a query key
 * discriminator. Ignores transient fields (signal, cursor) and normalises
 * sort order so that semantically identical configs produce the same key.
 */
function searchConfigKey(config: SearchConfig): string {
  return JSON.stringify({
    query: config.query,
    exactMatch: config.exactMatch,
    rows: config.rows,
    subscribed: config.subscribed,
    tags: config.tags?.slice().sort(),
    portfolioIds: config.portfolios?.map((p) => p.id).sort(),
    advancedFilterId: config.advancedFilter?.id,
    dateRange: config.dateRange,
  });
}

/**
 * Hook for searching headlines with SSE subscription support.
 * Uses React Query for data fetching and caching.
 *
 * The query key uses a serialized string derived from the search config
 * rather than the config object itself. This means:
 * - `setConfigKey(sameString)` is a React no-op (no re-render, no new query)
 * - React Query handles query lifecycle via key changes (no manual removeQueries)
 * - Abort signals cancel in-flight requests when the key changes
 */
export const useSearchHeadlines = (options?: UseSearchHeadlinesOptions) => {
  const { subscribe = true } = options || {};
  const { authenticated } = useAuth();
  const queryClient = useQueryClient();

  const [subscribeStatus, setSubscribeStatus] =
    useState<ConnectionStatus>("inactive");

  // Config ref: written in searchHeadlines(), read in queryFn and SSE handler.
  // Not state — the serialized configKey drives reactivity instead.
  const configRef = useRef<SearchConfig | null>(null);

  // Stable string key derived from the config. Drives the query key and
  // acts as a natural dedup: setConfigKey with the same string is a no-op.
  const [configKey, setConfigKey] = useState<string | null>(null);

  const searchQuery = useInfiniteQuery({
    queryKey: ["search-headlines", configKey],
    queryFn: async ({ pageParam, signal }) => {
      if (!configRef.current) {
        throw new Error("Search config not set");
      }
      return searchFullTextHeadlines({
        ...configRef.current,
        cursor: pageParam,
        signal,
      });
    },
    getNextPageParam: (lastPage: SearchResponse) => {
      return lastPage.pagination.has_more
        ? lastPage.pagination.next_cursor
        : undefined;
    },
    initialPageParam: undefined as string | undefined,
    enabled: authenticated && configKey !== null,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Flatten all pages into a single array of headlines
  const searchResults = useMemo(() => {
    if (!searchQuery.data?.pages) return [];
    return searchQuery.data.pages.reduce((acc: Headline[], page) => {
      return [...acc, ...page.data];
    }, []);
  }, [searchQuery.data]);

  // Get the last page's pagination info
  const pagination = useMemo(() => {
    if (!searchQuery.data?.pages) return undefined;
    const lastPage = searchQuery.data.pages[searchQuery.data.pages.length - 1];
    return lastPage?.pagination;
  }, [searchQuery.data]);

  const hasMore = useMemo(() => {
    return pagination?.has_more ?? false;
  }, [pagination]);

  /**
   * Trigger a new search with the given configuration.
   * If the config is semantically identical to the current one,
   * setConfigKey is a no-op and no new query is created.
   */
  const searchHeadlines = useCallback((config: SearchConfig) => {
    configRef.current = config;
    setConfigKey(searchConfigKey(config));
  }, []);

  /**
   * Load the next page of results
   */
  const loadNext = useCallback(() => {
    if (hasMore && !searchQuery.isFetchingNextPage) {
      searchQuery.fetchNextPage();
    }
  }, [hasMore, searchQuery]);

  /**
   * Clear search results and reset state
   */
  const clearSearchResults = useCallback(() => {
    configRef.current = null;
    setConfigKey(null);
    setSubscribeStatus("inactive");
  }, []);

  const canSubscribe =
    subscribe && authenticated && configRef.current?.subscribed;

  /**
   * Subscribe to SSE for live headline updates matching the search criteria
   */
  useEffect(() => {
    if (!canSubscribe || !configRef.current) return;

    const messageHandler = (data: Headline) => {
      if (!data) return;

      try {
        let matched = false;
        const config = configRef.current;
        if (!config) return;

        // Check if headline matches the current search criteria
        if (config.advancedFilter) {
          // Merge UI-selected portfolios and tags into the CategoryQuery
          // so headlineFilterMatcher evaluates them together, matching
          // the backend behavior in transformSearchParams.
          const filter = config.advancedFilter as CategoryQuery;
          const mergedFilter: CategoryQuery = {
            ...filter,
            portfolios: [
              ...(filter.portfolios || []),
              ...(config.portfolios || []),
            ],
            categories: [
              ...filter.categories,
              ...(config.tags || []).map(String),
            ],
          };
          matched = headlineFilterMatcher(data, mergedFilter);
        } else if (
          config.portfolios &&
          config.portfolios.length > 0
        ) {
          matched = headlinePortfolioMatcher(data, config.portfolios);
        }

        // Standalone tags fallback: only when no advancedFilter is active.
        // When advancedFilter IS active, tags are already merged into
        // the CategoryQuery's categories above.
        if (
          !matched &&
          !config.advancedFilter &&
          config.tags &&
          config.tags.length > 0 &&
          Array.isArray(data.tag_id_list)
        ) {
          const tagIdSet = new Set(config.tags);
          matched = data.tag_id_list.some((tagId) => tagIdSet.has(tagId));
        }

        if (!matched) return;

        // Mark as new
        const newHeadline = { ...data, isNew: true, received_at: Date.now() };

        // Update the query cache using the current queryKey
        const currentKey = ["search-headlines", configKey];
        queryClient.setQueryData(
          currentKey,
          (oldData: InfiniteData<SearchResponse> | undefined) => {
            if (!oldData) return oldData;

            // Check if headline already exists
            const headlineExists = oldData.pages.some((page) =>
              page.data.some((h: Headline) => h.id === newHeadline.id)
            );

            if (headlineExists) {
              // Update existing headline
              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  data: page.data.map((h: Headline) =>
                    h.id === newHeadline.id ? newHeadline : h
                  ),
                })),
              };
            }

            // Prepend new headline to the first page
            return {
              ...oldData,
              pages: oldData.pages.map((page, index: number) => {
                if (index !== 0) return page;
                const updated = [newHeadline, ...page.data];
                return {
                  ...page,
                  data: updated.length > MAX_FIRST_PAGE ? updated.slice(0, MAX_FIRST_PAGE) : updated,
                };
              }),
            };
          }
        );
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            component: "useSearchHeadlines",
            context: "sse_message_handler",
          },
          extra: { data },
        });
      }
    };

    const messageSub = headlinesSSEClient.subscribeTopic<Headline>({
      topic: "headline",
      callback: messageHandler,
    });

    const statusSub = headlinesSSEClient.onStatusChange(setSubscribeStatus);

    return () => {
      messageSub.unsubscribe();
      statusSub.unsubscribe();
    };
  }, [canSubscribe, configKey, queryClient]);

  return {
    searchResults,
    pagination,
    hasMore,
    searchHeadlines,
    clearSearchResults,
    loadNext,
    isLoadingMore: searchQuery.isFetchingNextPage,
    isLoading: searchQuery.isLoading,
    isError: searchQuery.isError,
    error: searchQuery.error,
    isSuccess: searchQuery.isSuccess,
    subscribeStatus,
    searchQuery,
  };
};
